import { Link } from 'react-router-dom';
import { Phone, Mail, Sparkles, Heart, Flame, ShieldCheck, ArrowRight, Utensils } from 'lucide-react';
import SEO from '../components/SEO';

export default function About() {
  return (
    <>
      <SEO
        title="Om os | Autentisk thai mad i Skibby | Bestil take-away eller en foodtruck"
        description="Preeya er vokset op i Thailand med en stor forkærlighed for det thailandske køkken, hvilket hun ønsker at dele med alle. Læs historien bag Thai Hot Chili."
      />

      {/* Header Banner */}
      <section className="relative pt-56 pb-16 bg-brand-dark text-white text-left">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/shutterstock-1407453566.jpg"
            alt="Thai Food Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-brand-dark via-brand-dark/90 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Om os
          </h1>
          <p className="text-white/90 font-display text-lg sm:text-xl mt-2 font-semibold">
            Autentisk thailandsk streetfood med sjæl
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-brand-cream text-brand-dark">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            {/* Left Column: Image */}
            <div className="lg:col-span-5 relative">
              <div className="relative bg-white p-3 rounded-2xl shadow-xl border border-gray-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/shutterstock-1407453566.jpg"
                  alt="Autentisk thailandsk madlavning"
                  className="w-full h-auto rounded-xl object-cover aspect-[4/3]"
                />
                <div className="absolute bottom-6 right-6 bg-brand-primary text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider shadow-md">
                  Friske Råvarer
                </div>
              </div>
            </div>

            {/* Right Column: Verbatim Text */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
                En lille smagsprøve på det ægte Thailand
              </h2>
              <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
                Preeya er vokset op i Thailand med en stor forkærlighed for det thailandske køkken, hvilket hun ønsker at dele med alle, så de kan få en lille smagsprøve på en af Thailands største forcer, nemlig den lækre mad.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Thai Hot Chili fokuserer på at servere autentisk thaimad, som det smager i de thailandske streetkitchens, med både traditionelle retter samt specialiteter lavet på friske råvarer.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Denne foodtruck kommer gerne ud til dig og servere den menu sammensætning du ønsker, eller vi kan lave maden til dig så det er klar til afhentning og du slipper for at stå i køkkenet hele dagen. Om det er barnedåb, konfirmation, bryllup, jubilæum rund fødselsdag eller firma arrangement stiller vi os gerne til rådighed.
              </p>

              <div className="border-t border-gray-200 pt-6 mt-2">
                <p className="font-display text-xl font-bold text-brand-primary italic">
                  Vi glæder os til at tage imod jer ☺
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-white text-brand-dark">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-20 flex flex-col gap-4">
            <h2 className="font-display text-3xl sm:text-4xl font-bold">
              Hvorfor vælge os?
            </h2>
            <p className="text-gray-500 text-base">
              Vi leverer mere end bare mad – vi leverer en autentisk thailandsk oplevelse med fokus på kvalitet og professionalisme.
            </p>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-12 lg:gap-16">
            {/* Column 1 */}
            <div className="flex flex-col items-center text-center gap-5 px-4">
              <Utensils className="w-10 h-10 text-brand-primary stroke-[1.25]" />
              <h3 className="font-display text-xl font-bold text-brand-dark">
                Autentisk thai mad
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                Thai Hot Chili fokuserer på at servere autentisk thaimad, som det smager i de thailandske streetkitchens, med både traditionelle retter samt specialiteter.
              </p>
            </div>

            {/* Mobile Divider 1 */}
            <div className="block md:hidden border-t border-gray-100 w-1/3 mx-auto" />

            {/* Desktop Divider 1 */}
            <div className="hidden md:block absolute left-1/3 top-6 bottom-6 w-[1px] bg-gray-100" />

            {/* Column 2 */}
            <div className="flex flex-col items-center text-center gap-5 px-4">
              <ShieldCheck className="w-10 h-10 text-brand-primary stroke-[1.25]" />
              <h3 className="font-display text-xl font-bold text-brand-dark">
                <Link to="/foodtruck-catering" className="hover:text-brand-primary transition-colors">
                  Foodtruck & catering
                </Link>
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                Vores professionelle foodtruck er fuldt udstyret og klar til at levere mad i topklasse til firmafester, bryllupper, fødselsdage, markeder og events.
              </p>
            </div>

            {/* Mobile Divider 2 */}
            <div className="block md:hidden border-t border-gray-100 w-1/3 mx-auto" />

            {/* Desktop Divider 2 */}
            <div className="hidden md:block absolute left-2/3 top-6 bottom-6 w-[1px] bg-gray-100" />

            {/* Column 3 */}
            <div className="flex flex-col items-center text-center gap-5 px-4">
              <Phone className="w-10 h-10 text-brand-primary stroke-[1.25]" />
              <h3 className="font-display text-xl font-bold text-brand-dark">
                <Link to="/kontakt" className="hover:text-brand-primary transition-colors">
                  Hurtig booking
                </Link>
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                Vi svarer hurtigt på alle henvendelser. Ring direkte til os eller udfyld vores kontaktformular, så finder vi den perfekte løsning til dig.
              </p>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}
